import React, { memo, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { fromJS, List } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import InputErrorDisplay from '../decorators/InputErrorDisplay';
import AutoCompleteContainer from '../AutoComplete';
import { unwrap, getIn, getChanged } from '../../utils/ChangeSpy';
import Popups from '../../components/Popup';
import isVisible from '../../utils/isVisible';
import {
  startEditContactEvent,
  changeContactEvent,
  saveContactEvent,
  findDirectors,
  findUsers,
  findAnalysts,
  closeValidationError,
  resetCurrentEvent,
} from '../../actions/contact/contactEvents';
import { publish } from '../../decorators/tabChange';
import { fetchEventActivities } from '../../actions/eventActivities';
import { usePopupContext } from '../../contexts/PopupContext';

const EventContactPopupContainer = ({
  children,
  contactInfo,
  contactBuyer,
  contactContacts,
  defaultAnalyst,
  defaultDirector,
  defaultDealMaker,
  events,
  funds,
  activities,
  backloggedActivities,
  user,
  popup,
  startEditContactEvent,
  changeContactEvent,
  saveContactEvent,
  findDirectors,
  findUsers,
  findAnalysts,
  resetCurrentEvent,
  fetchEventActivities,
  notifyTab,
}) => {
  const { closePopup, openPopup, onClosePopup } = usePopupContext();
  const needMoveFocusRef = useRef(false);

  const isValidDate = useCallback(date => {
    if (!date) return false;

    return !!date.isValid && date.isValid();
  }, []);

  const isNaExisting = useCallback(() => events.get('all').filter(event => unwrap(event.get('isNext'))).size > 0, [
    events,
  ]);

  const isValid = useCallback(
    (event, hasEverNext) => {
      if (!isValidDate(unwrap(event.get('date')))) return false;
      if (hasEverNext && !unwrap(event.get('completed'))) return false;

      return !!unwrap(event.getIn(['activityMap', 'selected', 'value']));
    },
    [isValidDate],
  );

  const moveFocus = useCallback(() => {
    const element = document.querySelector('textarea[name="description"]');

    // Check if element is visible in DOM, otherwise delay moving focus until DOM updated
    if (element && element.offsetParent) {
      element.focus();
    } else {
      needMoveFocusRef.current = true;
    }
  }, []);

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleCloseEventPopup = useCallback(() => {
    if (isNaExisting()) {
      handleClose();
      skipCloseCallbackRef.current = false;

      return;
    }

    if (skipCloseCallbackRef.current) {
      skipCloseCallbackRef.current = false;

      return;
    }

    openPopup('ConfirmPopup', {
      message:
        'Warning: You are about to close this window without setting a Next Action. Are you sure you wish to proceed?',
      onOk: handleClose,
      onCancel: popup.getIn(['props', 'onReopen'], () => {}),
      yes: 'Yes',
      no: 'No',
    });
  }, []);

  const getFundInfo = useCallback(
    body => {
      const event = events.get('current');
      const key = funds.findKey(f => unwrap(f.get('activeFund')));

      if (!event.get('id') && body.approach === 'exec' && key !== undefined && key !== null) {
        body.buyerId = funds.getIn([key, 'fundId'], 0);
        body.eprojectId = unwrap(funds.getIn([key, 'eprojectId'])) || 0;
      }
    },
    [events, funds],
  );

  const skipCloseCallbackRef = useRef(false);
  const reopenPopupRef = useRef(null);

  const reopenPopup = useCallback(
    approach => {
      openPopup('EventContactPopup', {
        contactId: popup.getIn(['props', 'contactId']),
        event: null,
        approach,
        noteText: popup.getIn(['props', 'noteText']),
        onReopen: popup.getIn(['props', 'onReopen']),
        isPromptReopen: true,
      });

      // onClosePopup(handleCloseEventPopup);
    },
    [popup, openPopup],
  );

  useEffect(() => {
    reopenPopupRef.current = reopenPopup;
  }, [reopenPopup]);

  const handleSave = useCallback(
    event => {
      event.preventDefault();

      const current = getChanged(events.get('current'));

      if (current.date) {
        current.date = current.date.format('YYYY-MM-DD');
      }

      Object.keys(current).forEach(key => {
        if (/Name$/.test(key)) {
          delete current[key];
        }
      });
      delete current.activityMap;

      const body = {
        ...current,
        id: events.getIn(['current', 'id']),
        approach: events.getIn(['current', 'approach']),
        activity: events.getIn(['current', 'activityMap', 'selected', 'name']),
        meta: false,
        backlogged: getIn(events, ['current', 'backlogged'], false) || false,
      };

      getFundInfo(body);

      body.execId = contactInfo.get('id');

      const form = event.target;

      for (let i = 0; i < form.length; i++) {
        if (!isVisible(form[i]) && form[i].name !== 'description') delete body[form[i].name];
      }
      ['recordOwnerId', 'recordSubOwnerId', 'dealMakerId'].forEach(field => {
        body[field] = body[field] || undefined;
      });

      saveContactEvent({
        entityType: 'contact',
        entityId: contactInfo.get('id'),
        body,
        afterSuccess: ({ getState }) => {
          const freshEvents = getState().contact.events;
          const naExists = freshEvents.get('all').filter(ev => unwrap(ev.get('isNext'))).size > 0;

          notifyTab(body, 'onEventChange');
          skipCloseCallbackRef.current = true;
          handleClose();

          if (!naExists) {
            reopenPopupRef.current(body.approach);
          }
        },
      });
    },
    [events, contactInfo],
  );

  // Refs to always call the latest closures from the mount-time listener
  const handleSaveRef = useRef(handleSave);
  const eventsRef = useRef(events);
  const isValidRef = useRef(isValid);

  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    isValidRef.current = isValid;
  }, [isValid]);

  useEffect(() => {
    const eventId = popup.getIn(['props', 'event', 'id']);
    const approach = popup.getIn(['props', 'approach']);
    const extra = {
      defaultUser: {
        id: user.get('id'),
        text: user.get('userName'),
      },
    };

    if (!eventId) {
      extra.defaultAnalyst = defaultAnalyst;
      extra.defaultDirector = defaultDirector;
      extra.defaultDealMaker = defaultDealMaker;
    }

    if (isNaExisting()) {
      extra.completed = true;
    }

    startEditContactEvent({
      eventId,
      approach: approach === 'executive' ? 'exec' : approach,
      contactInfo,
      contactBuyer,
      contactContacts,
      ...extra,
      afterSuccess: () => fetchEventActivities(),
    });

    setTimeout(() => {
      if (!isNaExisting()) {
        onClosePopup(handleCloseEventPopup);
      }
    }, 0);

    const handleCtrlEnter = event => {
      if (!event.ctrlKey || event.key !== 'Enter') return;

      const currentEvent = eventsRef.current.get('current');
      const hasEverNext =
        eventsRef.current.get('all').filter(ev => ev.get('id') !== currentEvent.get('id') && unwrap(ev.get('isNext')))
          .size > 0;

      if (isValidRef.current(currentEvent, hasEverNext)) {
        handleSaveRef.current(event);
      }
    };

    document.addEventListener('keydown', handleCtrlEnter, true);

    return () => {
      resetCurrentEvent();
      document.removeEventListener('keydown', handleCtrlEnter, true);
    };
  }, []);

  useEffect(() => {
    if (needMoveFocusRef.current) {
      moveFocus();
      needMoveFocusRef.current = false;
    }
  });

  const handleChange = useCallback(
    event => {
      const { name, type, checked, value } = event.target;

      changeContactEvent({
        field: name,
        value: type && type.toUpperCase() === 'CHECKBOX' ? checked : value,
      });

      if (name === 'completed' && checked) {
        changeContactEvent({ field: 'userName', value: user.get('userName') });
        changeContactEvent({ field: 'userId', value: user.get('id') });
      }
    },
    [changeContactEvent, user],
  );

  const handleActivityChange = useCallback(
    data => {
      changeContactEvent({ field: 'activityMap', value: data });
      changeContactEvent({ field: 'emailText.value', value: '' });
      if (data.selected && data.selected.value) {
        moveFocus();
      }
    },
    [changeContactEvent, moveFocus],
  );

  const handleSuggestChange = useCallback(({ name, value }) => changeContactEvent({ field: name, value }), [
    changeContactEvent,
  ]);

  const handleNext = useCallback(() => {
    moveFocus();
  }, [moveFocus]);

  const handleAddBuyerNote = useCallback(() => {
    const showEmail = !events.getIn(['current', 'activityMap', 'selected', 'showEmail']);

    changeContactEvent({ field: 'activityMap.selected.showEmail', value: showEmail });
  }, [events, changeContactEvent]);

  const prefix = 'current.';

  const suggestUser = (
    <AutoCompleteContainer
      idName={`${prefix}userId`}
      inputProps={{ name: 'userName', className: 'form-control', label: 'Completed by' }}
      name={`${prefix}userName`}
      onChange={handleSuggestChange}
      onFind={options => findUsers({ ...options, field: `${prefix}userName` })}
      rootPath={['contact', 'events']}
      suggestionsName={`${prefix}userSuggests`}
      valueName={`${prefix}userName`}
    />
  );

  const suggestDirector = (
    <AutoCompleteContainer
      idName={`${prefix}recordOwnerId`}
      inputProps={{ name: 'directorName', placeholder: 'Module', className: 'form-control', label: 'Team' }}
      name={`${prefix}directorName`}
      onChange={handleSuggestChange}
      onFind={options => findDirectors({ ...options, field: `${prefix}directorName` })}
      rootPath={['contact', 'events']}
      suggestionsName={`${prefix}directorSuggests`}
      valueName={`${prefix}directorName`}
    />
  );

  const suggestAnalyst = (
    <AutoCompleteContainer
      idName={`${prefix}recordSubOwnerId`}
      inputProps={{ name: 'analystName', label: 'Analyst', placeholder: 'Analyst', className: 'form-control' }}
      name={`${prefix}analystName`}
      onChange={handleSuggestChange}
      onFind={options => findAnalysts({ ...options, field: `${prefix}analystName` })}
      rootPath={['contact', 'events']}
      suggestionsName={`${prefix}analystSuggests`}
      valueName={`${prefix}analystName`}
    />
  );

  const currentEvent = events.get('current');
  const hasEverNext =
    events.get('all').filter(event => event.get('id') !== currentEvent.get('id') && unwrap(event.get('isNext'))).size >
    0;
  const contacts = fromJS([{ name: unwrap(contactInfo.get('fullName')), value: contactInfo.get('id') }]);
  const canSave = isValid(currentEvent, hasEverNext);

  return (
    <>
      <Popups.EventPopup
        activities={activities}
        backloggedActivities={backloggedActivities}
        canSave={canSave}
        contacts={contacts}
        event={currentEvent}
        funds={funds}
        hasEverNext={hasEverNext}
        loading={currentEvent.get('isLoading')}
        noteText={popup.getIn(['props', 'noteText'])}
        onActivityChange={handleActivityChange}
        onAddBuyerNote={handleAddBuyerNote}
        onChange={handleChange}
        onClose={handleCloseEventPopup}
        onNext={handleNext}
        onSave={handleSave}
        shouldPrompt={false}
        suggestAnalyst={suggestAnalyst}
        suggestDirector={suggestDirector}
        suggestUser={suggestUser}
      />
      {children}
    </>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    defaultDirector: {
      id: state.contact.executive.getIn(['recordOwnerId', 'value']),
      text: state.contact.executive.getIn(['suggestDirector', 'value']),
    },
    defaultAnalyst: {
      id: state.contact.executive.getIn(['recordSubOwnerId', 'value']),
      text: state.contact.executive.getIn(['suggestAnalyst', 'value']),
    },
    defaultDealMaker: {
      id: state.contact.executive.getIn(['dealMakerId', 'value']),
      text: state.contact.executive.getIn(['suggestDealMaker', 'value']),
    },
    contactInfo: state.contact.info,
    entityId: state.contact.info.getIn(['id']),
    events: state.contact.events,
    inputErrors: state.contact.events.getIn(['current', 'inputErrors']),
    funds: state.contact.executive.get('funds'),
    user: state.auth.get('user', null),
    activities: state.eventActivities.getIn(['activities', 'all'], List()),
    backloggedActivities: state.eventActivities.get('backloggedActivityValues', List()),
  };
}

export { EventContactPopupContainer };

const publishedWrap = publish(memo(EventContactPopupContainer));

export default connect(
  mapStateToProps,
  {
    startEditContactEvent,
    changeContactEvent,
    saveContactEvent,
    findDirectors,
    findUsers,
    findAnalysts,
    closeValidationError,
    resetCurrentEvent,
    fetchEventActivities,
  },
  mergeProps,
  connectOptions,
)(InputErrorDisplay('closeValidationError', publishedWrap));
